import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DayPicker } from 'react-day-picker';
import { Loader2, Route, Calendar as CalendarIcon } from 'lucide-react';
import 'react-day-picker/dist/style.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Visual booking calendar (Session 27).
 *
 * Renders a month grid with each day color-coded by status fetched from
 * GET /providers/{id}/availability-calendar?from=&to=
 *
 * Statuses:
 *   - available  → green dot, selectable
 *   - route_day  → blue badge with route name, selectable
 *   - full       → grey "Full", not selectable
 *   - blocked    → grey strike-through, not selectable (vacation/emergency-only/etc)
 *   - unavailable → no styling, not selectable (provider doesn't work that day)
 *
 * Props:
 *   providerId: required
 *   onSelectDate: (yyyy-mm-dd, dayMeta) => void
 *   value: optional yyyy-mm-dd string (controlled selected date)
 *   minDate: optional Date (default = today)
 */
export default function BookingCalendar({ providerId, onSelectDate, value, minDate }) {
  const [month, setMonth] = useState(new Date());
  const [days, setDays] = useState({});  // { 'YYYY-MM-DD': {status, slotsRemaining, routeName, reason} }
  const [loading, setLoading] = useState(false);
  const min = minDate || new Date();
  min.setHours(0, 0, 0, 0);

  // Load the visible month's data (plus a small buffer on either side so
  // adjacent-month days in the grid render correctly).
  useEffect(() => {
    if (!providerId) return;
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const last  = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const fromD = new Date(first); fromD.setDate(fromD.getDate() - 7);
    const toD   = new Date(last);  toD.setDate(toD.getDate() + 7);
    const toISO = (d) => d.toISOString().slice(0, 10);

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/api/providers/${providerId}/availability-calendar`,
          { params: { from_date: toISO(fromD), to_date: toISO(toD) } }
        );
        if (cancelled) return;
        const map = {};
        for (const d of res.data) map[d.date] = d;
        setDays(map);
      } catch (err) {
        console.error('Calendar load failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [providerId, month]);

  const modifiers = useMemo(() => {
    const acc = { available: [], route: [], full: [], blocked: [] };
    for (const [date, meta] of Object.entries(days)) {
      const d = new Date(date + 'T12:00:00');
      if (d < min) continue;
      if (meta.status === 'available')   acc.available.push(d);
      if (meta.status === 'route_day')   acc.route.push(d);
      if (meta.status === 'full')        acc.full.push(d);
      if (meta.status === 'blocked')     acc.blocked.push(d);
    }
    return acc;
  }, [days, min]);

  const disabledDays = useMemo(() => {
    const list = [{ before: min }];
    // Disable everything that isn't available or a route day
    for (const [date, meta] of Object.entries(days)) {
      if (meta.status === 'available' || meta.status === 'route_day') continue;
      list.push(new Date(date + 'T12:00:00'));
    }
    return list;
  }, [days, min]);

  const handleSelect = (d) => {
    if (!d) return;
    const iso = d.toISOString().slice(0, 10);
    const meta = days[iso];
    if (!meta || (meta.status !== 'available' && meta.status !== 'route_day')) return;
    onSelectDate?.(iso, meta);
  };

  const selectedDate = value ? new Date(value + 'T12:00:00') : undefined;
  const selectedMeta = value ? days[value] : null;

  return (
    <div className="rounded-lg border bg-card" data-testid="booking-calendar">
      <div className="p-3 sm:p-4">
        <DayPicker
          mode="single"
          month={month}
          onMonthChange={setMonth}
          selected={selectedDate}
          onSelect={handleSelect}
          disabled={disabledDays}
          modifiers={modifiers}
          modifiersClassNames={{
            available: 'bk-cal-available',
            route:     'bk-cal-route',
            full:      'bk-cal-full',
            blocked:   'bk-cal-blocked',
          }}
          showOutsideDays
          className="rdp-mini"
        />
        {loading && (
          <div className="flex items-center justify-center pt-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Loading availability...
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="border-t px-3 sm:px-4 py-2 text-xs flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-emerald-500" /> Available
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-sky-500" /> Route day
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-slate-300" /> Full
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-slate-200 line-through" /> Blocked
        </span>
      </div>

      {/* Selected day context */}
      {selectedMeta && selectedMeta.status === 'route_day' && (
        <div
          className="border-t bg-sky-50 px-3 py-2 text-xs sm:text-sm text-sky-900 flex items-center gap-2"
          data-testid="route-day-banner"
        >
          <Route className="h-4 w-4 shrink-0" />
          <span>
            <strong>{selectedMeta.routeName}</strong>
            {typeof selectedMeta.slotsRemaining === 'number' && selectedMeta.slotsRemaining > 0
              ? ` — ${selectedMeta.slotsRemaining} ${selectedMeta.slotsRemaining === 1 ? 'spot' : 'spots'} left`
              : ''}
          </span>
        </div>
      )}
      {selectedMeta && selectedMeta.status === 'available' && selectedMeta.slotsRemaining > 0 && selectedMeta.slotsRemaining <= 3 && (
        <div
          className="border-t bg-amber-50 px-3 py-2 text-xs sm:text-sm text-amber-800 flex items-center gap-2"
          data-testid="limited-spots-banner"
        >
          <CalendarIcon className="h-4 w-4 shrink-0" />
          Only {selectedMeta.slotsRemaining} {selectedMeta.slotsRemaining === 1 ? 'spot' : 'spots'} left this day
        </div>
      )}
    </div>
  );
}
